import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { UserSplit } from '@modernloop/shared/icons';
import { Avatar, Chip, IconButton, List, ListItem, ListSubheader, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';

import { DynamicAudience, OptionRenderer_EmployeesFragment } from 'src/generated/mloop-graphql';

import OptionalAttendeeToggle from 'src/entities/common/OptionalAttendeeToggle';

import { SmartEmployeeSelectChipAction } from './chipAction';
import { chipGroupSx } from './chipLayout';
import { HoverPopover } from './HoverPopover';
import { DynamicAudienceContext } from './types';
import { DynamicAudienceChipIconVariant, getDacIcon, getSmartOptionLabel } from './utils';

type Props = {
  context: DynamicAudienceContext;
  dynamicAudience: DynamicAudience;
  renderData: OptionRenderer_EmployeesFragment[] | null | undefined;
  onDelete: (item: DynamicAudience) => void;
  onSplit: (item: DynamicAudience) => void;
  /**
   * Opt-in required/optional control. Settings surfaces pass one, because there an audience is a
   * recipient carrying the role its members will inherit. Communications never do: there the
   * audience has already been resolved and each member carries their own role.
   */
  action?: SmartEmployeeSelectChipAction;
};

const getEmployeeName = (employee?: OptionRenderer_EmployeesFragment) =>
  employee?.fullName ?? employee?.givenName ?? employee?.familyName ?? '';

export const DynamicAudienceChip: FC<Props> = ({ dynamicAudience, renderData, onDelete, context, onSplit, action }) => {
  const { t } = useTranslation();
  const multipleEmployees = !!renderData && !isEmpty(renderData) && renderData?.length > 1;
  const singleEmployeeInArray = !!renderData && !isEmpty(renderData) && renderData?.length === 1;

  let labelJsx = <>{getSmartOptionLabel(dynamicAudience, t)}</>;
  if (multipleEmployees) {
    labelJsx = (
      <Stack alignItems="center" direction="row">
        {getSmartOptionLabel(dynamicAudience, t)}{' '}
        {t('hiring-dashboard-common:common.smartOption.peopleCount', { count: renderData?.length })}{' '}
        <IconButton size="small" edge="end" onClick={() => onSplit(dynamicAudience)}>
          <UserSplit tooltip={t('hiring-dashboard-common:common.smartOption.split')} />
        </IconButton>
      </Stack>
    );
  } else if (singleEmployeeInArray) {
    labelJsx = (
      <Stack>
        {getSmartOptionLabel(dynamicAudience, t)} ({getEmployeeName(renderData?.[0])})
      </Stack>
    );
  }

  let tooltip: React.ReactNode = '';
  if (multipleEmployees) {
    tooltip = (
      <List>
        <ListSubheader sx={{ px: 0 }}>{t('hiring-dashboard-common:common.smartOption.inThisGroup')}</ListSubheader>
        <Stack
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          {renderData?.map((employee) => (
            <ListItem key={employee.id}>
              <Stack direction="row" alignItems="center" columnGap={1}>
                <Avatar src={employee.internalProfilePictureUrl ?? ''} alt={employee.fullName || ''} />
                <Typography color="text.secondary">{employee.fullName}</Typography>
              </Stack>
            </ListItem>
          ))}
        </Stack>
      </List>
    );
  } else if (isEmpty(renderData) && !context.enableOptionsWithMissingData) {
    tooltip = t('hiring-dashboard-common:common.smartOption.noPersonAssigned');
  }

  const chip = (
    <HoverPopover title={tooltip}>
      <Chip
        variant={isEmpty(renderData) && !context.enableOptionsWithMissingData ? 'outlined' : 'filled'}
        icon={getDacIcon(
          dynamicAudience,
          isEmpty(renderData) && !context.enableOptionsWithMissingData
            ? DynamicAudienceChipIconVariant.Error
            : DynamicAudienceChipIconVariant.Primary
        )}
        size="small"
        color={isEmpty(renderData) && !context.enableOptionsWithMissingData ? 'error' : 'default'}
        label={labelJsx}
        onClick={
          action
            ? undefined
            : (e) => {
                e.stopPropagation();
              }
        }
        onDelete={() => {
          onDelete(dynamicAudience);
        }}
      />
    </HoverPopover>
  );

  // Settings mode only. The toggle sits beside the chip rather than inside it, and the chip drops
  // its own onClick so the group holds exactly two controls: the toggle and the chip's delete.
  return action ? (
    <Stack alignItems="center" direction="row" columnGap={0.5} sx={chipGroupSx}>
      {chip}
      <OptionalAttendeeToggle isAttendeeOptional={action.isOptional} onUserIconClick={action.onToggle} />
    </Stack>
  ) : (
    chip
  );
};
